import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { updateWithDomainAndQueryParams } from '@fundera/fundera-utilities';
import { isMonetizedLink } from '@fundera/fundera-utilities/url';
import { AppContext } from '../../providers/AppContext';
import { Context as AcfContext } from '../../providers/individual-acf-context';
import usePageViewId from '../../hooks/usePageViewId';

const pickBy = (object, condition) => {
  return Object.keys(object)
    .filter(key => condition(object[key]))
    .map(key => ({ [key]: object[key] }))
    .reduce((o1, o2) => ({ ...o1, ...o2 }), {});
};

/**
 * @param {Object} props properties passed to the component
 * @param {string} props.href href of the link
 * @param {string} props.target target to use in the button
 * @param {string} props.componentType name of the component/ACF to pass as placement type
 * @param {Number} props.rank rank/position of the link
 * @param {Number} props.moduleRank module rank/position of the link within the module/component
 * @param {string} props.rel class to use
 * @param {boolean} props.ignoreRank when to ignore rank,
 * data-rank and data-module-rank won't be added if true
 * @param {boolean} props.ignoreData when to ignore data,
 * @param {boolean} props.ignoreMvisit when to not set data-mvisit
 * no data attributes about the impression tags will be added if true
 * @param {Object} children children nodes/components
 * @returns {React.FC} A component used to create a redirect button to link provided.
 */

/* eslint-disable react/jsx-props-no-spreading */
const MonetizedLink = props => {
  const {
    href,
    target,
    ignoreRank,
    ignoreData,
    ignoreMvisit,
    rank,
    moduleRank,
    componentType,
    rel,
    children,
    component,
    ...otherProps
  } = props;
  const Component = component || 'a';
  const appContext = useContext(AppContext);
  const acfContext = useContext(AcfContext);
  const pageViewId = usePageViewId();
  const slug = appContext && appContext.slug;
  const finalComponentType =
    componentType || (acfContext && acfContext.componentType);
  const finalRank = rank || (acfContext && acfContext.rank);
  const monetized = isMonetizedLink(href);
  const validate = () => {
    if (!monetized) return;
    if (!finalComponentType) {
      // eslint-disable-next-line no-console
      console.error(
        new Error('No component type informed by props or AcfContext.')
      );
    }
    if (!ignoreData && !ignoreRank && !finalRank) {
      // eslint-disable-next-line no-console
      console.error(
        new Error(
          `No rank informed by props or AcfContext for ${finalComponentType}. Set ignoreRank to true if the component or link should not be ranked.`
        )
      );
    }
  };

  const getDataProps = () => {
    if (!monetized || ignoreData) return {};
    const matchProductId = /(\d+)/.exec(href);
    return pickBy(
      {
        'data-product-id': matchProductId && matchProductId[0],
        'data-placement-type': finalComponentType,
        'data-page-path': slug,
        'data-rank': !ignoreRank && finalRank,
        'data-module-rank': !ignoreRank && moduleRank,
        'data-pageviewid': pageViewId,
        'data-mvisit': !ignoreMvisit,
      },
      Boolean
    );
  };
  validate();
  const dataProps = getDataProps();
  return (
    <Component
      href={updateWithDomainAndQueryParams(href, {
        placement: slug,
        placement_type: finalComponentType,
        page_view_id: {
          value: pageViewId,
          force: true,
        },
      })}
      rel={rel}
      target={target}
      {...otherProps}
      {...dataProps}
    >
      {children}
    </Component>
  );
};

MonetizedLink.propTypes = {
  href: PropTypes.string.isRequired,
  target: PropTypes.string,
  rel: PropTypes.string,
  componentType: PropTypes.string,
  rank: PropTypes.number,
  moduleRank: PropTypes.number,
  ignoreRank: PropTypes.bool,
  ignoreData: PropTypes.bool,
  ignoreMvisit: PropTypes.bool,
  children: PropTypes.node,
  component: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
};

MonetizedLink.defaultProps = {
  target: '_blank',
  rel: 'noreferrer noopener',
  componentType: undefined,
  rank: undefined,
  moduleRank: 1,
  ignoreRank: false,
  ignoreData: false,
  ignoreMvisit: false,
  children: undefined,
  component: 'a',
};

export default MonetizedLink;
